import { ctaDesc, ctaTitle } from "@/lib/seo";
// /en/industrial-estates — EN hub for Thailand industrial estates
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { INDUSTRIAL_ESTATES } from "@/data/industrial-estates";
import { Building2, MapPin, Phone, MessageCircle, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/industrial-estates/")({
  head: () => {
    const path = "/en/industrial-estates";
    const thPath = "/industrial-estates";
    const title = "Thailand Industrial Estates — Notary · Translation · Work Permit · MFA | NYC";
    const desc = `Bilingual legal, notary, certified translation and work-permit support for foreign-invested plants in 20+ Thai industrial estates — EEC (Amata, WHA, Hemaraj, Map Ta Phut), Bangkok (Lat Krabang, Bangpoo, Bangchan), Ayutthaya (Rojana) and Lamphun. Daily on-site courier, ISO 17100 EN/JP/CN/KR translation, MFA legalisation in 3 days. Send your documents for a free check.`;
    const url = `${SITE}${path}`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/industrial-estates", "en") }, { name: "description", content: ctaDesc(desc, "/en/industrial-estates", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/industrial-estates", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/industrial-estates", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { name: "keywords", content: "Thailand industrial estate, EEC notary, Amata work permit, WHA translation, Map Ta Phut MFA, Lat Krabang BOI, foreign workforce Thailand" },
      ],
      links: [
        { rel: "canonical", href: `${SITE}${path}` },
        { rel: "alternate", hrefLang: "th-TH", href: `${SITE}${thPath}` },
        { rel: "alternate", hrefLang: "en", href: `${SITE}${path}` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}${thPath}` },
      ],
      scripts: [
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#collection`,
            name: title, description: desc, url, inLanguage: "en-US",
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: INDUSTRIAL_ESTATES.length,
              itemListElement: INDUSTRIAL_ESTATES.map((e, i) => ({
                "@type": "ListItem", position: i + 1, name: e.nameEn,
                url: `${SITE}/en/industrial-estates/${e.slug}`,
              })),
            },
          }),
        },
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
            mainEntity: [
              { "@type": "Question", name: "Which Thai industrial estates does NYC cover?",
                acceptedAnswer: { "@type": "Answer", text: `${INDUSTRIAL_ESTATES.length}+ major estates across the EEC (Amata, WHA, Hemaraj, Map Ta Phut, Laem Chabang, Gateway City), Central (Lat Krabang, Bangpoo, Bangplee, Navanakorn, Rojana), Northern (Lamphun), Northeast (Suranaree) and Southern (Songkhla) regions.` } },
              { "@type": "Question", name: "What services do plants in these estates use most?",
                acceptedAnswer: { "@type": "Answer", text: "Work Permit + BOI Visa for Japanese/Chinese/Korean engineers, ISO 17100 technical translation, MFA legalisation of Export Certs and COOs, notarisation of POAs and Board Resolutions, and on-site interpreting." } },
              { "@type": "Question", name: "Do you collect documents on-site?",
                acceptedAnswer: { "@type": "Answer", text: "Yes — NYC runs daily couriers across EEC, Lat Krabang–Suvarnabhumi, Navanakorn–Don Mueang and Lamphun–CNX. Courier is free above THB 5,000 per job." } },
              { "@type": "Question", name: "What are typical turnaround times?",
                acceptedAnswer: { "@type": "Answer", text: "Notarisation same-day, technical translation 1-2 days, MFA legalisation 2-3 business days, new work permits 7-10 business days depending on the provincial labour office." } },
            ],
          }),
        },
      ],
    };
  },
  component: IndustrialEstatesHubEn,
});

function IndustrialEstatesHubEn() {
  const byRegion = INDUSTRIAL_ESTATES.reduce<Record<string, typeof INDUSTRIAL_ESTATES>>((acc, e) => {
    (acc[e.region] ||= []).push(e); return acc;
  }, {});
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Building2 className="mr-1 inline h-3 w-3" /> B2B · Industrial Estates
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            End-to-end legal & translation for {INDUSTRIAL_ESTATES.length}+ Thai industrial estates
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Notarisation · ISO 17100 translation (EN/JP/CN/KR) · BOI work-permits · MFA legalisation —
            NYC runs daily on-site couriers across EEC, Bangkok, Ayutthaya, Lamphun and Songkhla.
          </p>
          <div className="quick-answer mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">How does a plant in a Thai industrial estate engage NYC?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Message LINE @nyc-notary or call +66 92-242-9293. Our courier reaches your plant the next business day; certified translation + notarisation are returned within 1-2 days, MFA legalisation in 3 days, work permits in 7-10 business days. 99.6% first-time acceptance across.
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Choose your industrial estate</h2>
          <p className="mt-2 text-sm text-muted-foreground">Grouped by region · click an estate for site-specific pricing and courier routes</p>
          <div className="mt-8 space-y-10">
            {Object.entries(byRegion).map(([region, list]) => (
              <div key={region}>
                <h3 className="mb-4 text-lg font-bold text-primary"><MapPin className="mr-1 inline h-4 w-4" />{region}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((e) => (
                    <Link
                      key={e.slug}
                      to="/en/industrial-estates/$slug"
                      params={{ slug: e.slug }}
                      className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                      data-track-zone="industrial-hub-en"
                    >
                      <div className="font-bold">{e.nameEn}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{e.provinceEn} · {e.zoneType}</div>
                      <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{e.industries.slice(0, 3).join(" · ")}</div>
                      <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                        View services <ArrowRight className="h-3 w-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />Most-requested services on-site</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Work Permit + BOI Visa", d: "JP/CN/KR/IN engineers — 7-10 days", to: "/en/work-permit" as const },
              { t: "Technical Translation", d: "ISO Manual, MSDS, HAZOP, ECO — ISO 17100", to: "/en/translate" as const },
              { t: "MFA + Embassy Legalisation", d: "COO, Export Cert, POA — 3-5 days", to: "/en/translate/mfa" as const },
              { t: "Notarisation (POA, Board)", d: "Same-day Notarial Services Attorney", to: "/en/notary" as const },
              { t: "Apostille (Hague)", d: "For JV / partner countries", to: "/en/apostille" as const },
              { t: "APEC Card (ABTC)", d: "Plant executives, 21 economies", to: "/en/apec" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    <RelatedHubGrid locale="en" /></main>
  );
}
